import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Centre } from '../../../interfaces/Centres';
import { CentreService } from '../../../services/centre.service';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';

type Timestamp = firebase.firestore.Timestamp;

interface DocumentItem {
  description: string;
  fileName: string;
  url: string;
  date?: any;
  dateAdded?: any;
  // Properties added during flattening
  sessionId?: string;
  sessionDate?: any;
  centreCode?: string;
  centreName?: string;
}

interface SessionDocument {
  id: string;
  sessionId: string;
  date: any;
  centreCode?: string;
  centreName?: string;
  documents: DocumentItem[];
}

@Component({
  selector: 'app-counselling-photo-gallery',
  templateUrl: './counselling-photo-gallery.component.html',
  styleUrls: ['./counselling-photo-gallery.component.scss']
})
export class CounsellingPhotoGalleryComponent implements OnInit, OnDestroy {
  private _destroyed$ = new Subject<void>();
  centres$: Observable<Centre[]>;
  selectedCentre: string = 'All';
  startDate: Date | null = null;
  endDate: Date | null = null;
  sessionType: 'individual' | 'group' = 'individual';

  allDocuments: SessionDocument[] = [];
  filteredDocuments: SessionDocument[] = [];
  displayedDocuments: DocumentItem[] = [];

  isLoading: boolean = false;

  // For search and filter
  allCentres: Centre[] = [];
  filteredCentres: Centre[] = [];
  searchTextCentre: string = '';

  // For image selection
  selectedImages: string[] = [];
  selectedDocuments: DocumentItem[] = [];
  isSelectionMode: boolean = false;

  // View mode
  viewMode: 'grid' | 'list' = 'grid';

  // Performance tracking
  loadStartTime: number = 0;
  loadEndTime: number = 0;
  loadDuration: number = 0;

  constructor(
    private firestore: AngularFirestore,
    private centreService: CentreService,
    public dialogRef: MatDialogRef<CounsellingPhotoGalleryComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.centres$ = this.centreService.getAllCentres();

    // If data passed, set defaults
    if (data) {
      this.selectedCentre = data.centreCode || 'All';
      this.sessionType = data.sessionType || 'individual';
      this.startDate = data.startDate || null;
      this.endDate = data.endDate || null;
    }
  }

  ngOnInit(): void {
    this.centres$.pipe(takeUntil(this._destroyed$)).subscribe((centres: Centre[]) => {
      this.allCentres = centres || [];
      this.filteredCentres = this.getSortedCentres(this.allCentres);
    });

    // Auto-load if dates are provided
    if (this.startDate && this.endDate) {
      this.fetchDocuments();
    }
  }

  /**
   * Fetch documents from Firestore based on filters
   */
  async fetchDocuments(): Promise<void> {
    if (!this.startDate || !this.endDate) {
      alert('Please select both start and end dates');
      return;
    }

    this.isLoading = true;
    this.loadStartTime = Date.now();
    this.allDocuments = [];
    this.filteredDocuments = [];
    this.displayedDocuments = [];

    try {
      const documentsPath = this.sessionType === 'individual'
        ? 'ALDocuments/Counselling/individualSessions'
        : 'ALDocuments/Counselling/groupSessions';

      console.log('🔍 Fetching documents from:', documentsPath);
      console.log('📅 Date range:', this.startDate, 'to', this.endDate);

      // Set start date to beginning of day and end date to end of day
      const startOfDay = new Date(this.startDate);
      startOfDay.setHours(0, 0, 0, 0);

      const endOfDay = new Date(this.endDate);
      endOfDay.setHours(23, 59, 59, 999);

      // Convert dates to timestamps for comparison
      const startTimestamp = firebase.firestore.Timestamp.fromDate(startOfDay);
      const endTimestamp = firebase.firestore.Timestamp.fromDate(endOfDay);

      console.log('🕐 Timestamp range:', startTimestamp.toDate(), 'to', endTimestamp.toDate());

      // Fetch documents from ALDocuments collection with date filter
      let documentsSnapshot = await this.firestore
        .collection(documentsPath)
        .ref
        .where('date', '>=', startTimestamp)
        .where('date', '<=', endTimestamp)
        .get();

      console.log(`📎 Found ${documentsSnapshot.docs.length} document records in ${documentsPath}`);

      if (documentsSnapshot.docs.length === 0) {
        console.warn('⚠️ No documents found in the selected date range');
        this.isLoading = false;
        return;
      }

      // Process each document record - SIMPLIFIED for speed
      for (const docRecord of documentsSnapshot.docs) {
        const docData = docRecord.data() as any;

        const documents = docData.documents || [];

        if (documents.length === 0) {
          continue; // Skip if no files
        }

        console.log(`📄 Document ${docRecord.id}: ${documents.length} file(s), Centre: ${docData.centreName || 'N/A'}`);

        // Use centreName and centreCode directly from document root
        const sessionDoc: SessionDocument = {
          id: docRecord.id,
          sessionId: docRecord.id,
          date: docData.date,
          centreCode: docData.centreCode || 'Unknown',
          centreName: docData.centreName || 'Unknown Centre',
          documents: documents
        };

        this.allDocuments.push(sessionDoc);
      }

      console.log(`✅ Processed ${this.allDocuments.length} session documents`);

      // Apply centre filter
      this.applyFilters();

      console.log(`✅ Final: ${this.displayedDocuments.length} documents to display`);

      if (this.displayedDocuments.length === 0) {
        console.warn('⚠️ No documents to display after filtering. Check filters and data structure.');
      }

      // Calculate load duration
      this.loadEndTime = Date.now();
      this.loadDuration = (this.loadEndTime - this.loadStartTime) / 1000;
      console.log(`⏱️ Load completed in ${this.loadDuration.toFixed(2)} seconds`);

    } catch (error: any) {
      console.error('❌ Error fetching documents:', error);
      alert(`Error: ${error.message || 'Failed to fetch documents'}`);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Apply centre filter to documents
   */
  applyFilters(): void {
    console.log(`🔍 Applying filters - Selected Centre: ${this.selectedCentre}`);
    console.log(`📊 Total documents before filter: ${this.allDocuments.length}`);

    if (this.selectedCentre === 'All') {
      this.filteredDocuments = [...this.allDocuments];
      console.log(`  ✓ Showing all documents`);
    } else {
      this.filteredDocuments = this.allDocuments.filter(doc => {
        // Handle both single centre and multiple centres (for group sessions)
        if (doc.centreCode) {
          // Check if centreCode matches or is included in comma-separated list
          const matches = doc.centreCode === this.selectedCentre ||
                 doc.centreCode.includes(this.selectedCentre);

          if (matches) {
            console.log(`  ✓ Document ${doc.id} matches centre filter (${doc.centreCode})`);
          }

          return matches;
        }

        console.log(`  ⚠️ Document ${doc.id} has no centreCode`);
        return false;
      });

      console.log(`  ✓ Filtered to ${this.filteredDocuments.length} documents for centre ${this.selectedCentre}`);
    }

    // Flatten documents for display
    this.displayedDocuments = this.filteredDocuments.flatMap(doc => {
      const flattened = doc.documents.map(d => ({
        ...d,
        sessionId: doc.sessionId,
        sessionDate: doc.date,
        centreCode: doc.centreCode,
        centreName: doc.centreName
      }));

      console.log(`  📄 Session ${doc.id}: ${flattened.length} file(s)`);
      return flattened;
    });

    console.log(`✅ Final displayed documents: ${this.displayedDocuments.length}`);
  }

  /**
   * Search functionality
   */
  onSearch(): void {
    console.log('🔍 Search triggered with:', {
      sessionType: this.sessionType,
      centre: this.selectedCentre,
      startDate: this.startDate,
      endDate: this.endDate
    });
    this.fetchDocuments();
  }

  /**
   * Debug method to check collection structure
   */
  async debugCollection(): Promise<void> {
    const documentsPath = this.sessionType === 'individual'
      ? 'ALDocuments/Counselling/individualSessions'
      : 'ALDocuments/Counselling/groupSessions';

    console.log('🔧 DEBUG: Checking collection:', documentsPath);

    try {
      const snapshot = await this.firestore
        .collection(documentsPath)
        .ref
        .limit(3)
        .get();

      console.log('🔧 DEBUG: Sample documents:', snapshot.docs.map(d => ({
        id: d.id,
        data: d.data()
      })));
    } catch (error) {
      console.error('🔧 DEBUG: Error fetching collection:', error);
    }
  }

  /**
   * Clear all filters
   */
  onClear(): void {
    this.selectedCentre = 'All';
    this.startDate = null;
    this.endDate = null;
    this.allDocuments = [];
    this.filteredDocuments = [];
    this.displayedDocuments = [];
    this.selectedImages = [];
  }

  /**
   * Centre selection change
   */
  onCentreChange(centre: string): void {
    this.selectedCentre = centre;
    this.applyFilters();
  }

  /**
   * Session type change
   */
  onSessionTypeChange(type: 'individual' | 'group'): void {
    this.sessionType = type;
    if (this.startDate && this.endDate) {
      this.fetchDocuments();
    }
  }

  /**
   * Centre search
   */
  onCentreSearchChange(event: any): void {
    this.searchTextCentre = event.target.value;
    this.filterCentres();
  }

  onCentreSelectOpened(opened: boolean): void {
    if (opened) {
      this.searchTextCentre = '';
      this.filterCentres();
    }
  }

  filterCentres(): void {
    let sorted = this.getSortedCentres(this.allCentres);
    if (this.searchTextCentre) {
      sorted = sorted.filter(c =>
        c.centreName.toLowerCase().includes(this.searchTextCentre.toLowerCase())
      );
    }
    this.filteredCentres = sorted;
  }

  getSortedCentres(list: Centre[]): Centre[] {
    return [...list].sort((a, b) =>
      a.centreName.trim().toLowerCase().localeCompare(b.centreName.trim().toLowerCase())
    );
  }

  /**
   * Image selection for multi-select
   */
  selectImage(url: string): void {
    if (!this.isSelectionMode) {
      // If not in selection mode, just open the image
      this.openImage(url);
      return;
    }

    const index = this.selectedImages.indexOf(url);
    if (index > -1) {
      // Deselect
      this.selectedImages.splice(index, 1);
      const docIndex = this.selectedDocuments.findIndex(doc => doc.url === url);
      if (docIndex > -1) {
        this.selectedDocuments.splice(docIndex, 1);
      }
    } else {
      // Select
      this.selectedImages.push(url);
      const doc = this.displayedDocuments.find(d => d.url === url);
      if (doc) {
        this.selectedDocuments.push(doc);
      }
    }
  }

  isSelected(url: string): boolean {
    return this.selectedImages.includes(url);
  }

  /**
   * Select all images
   */
  selectAll(): void {
    this.selectedImages = this.displayedDocuments.map(doc => doc.url);
    this.selectedDocuments = [...this.displayedDocuments];
    this.isSelectionMode = true;
  }

  /**
   * Deselect all images
   */
  deselectAll(): void {
    this.selectedImages = [];
    this.selectedDocuments = [];
  }

  /**
   * Enable selection mode
   */
  enableSelectionMode(): void {
    this.isSelectionMode = true;
    this.selectedImages = [];
    this.selectedDocuments = [];
  }

  /**
   * Cancel selection mode
   */
  cancelSelection(): void {
    this.isSelectionMode = false;
    this.selectedImages = [];
    this.selectedDocuments = [];
  }

  /**
   * Confirm selection and return selected documents
   */
  confirmSelection(): void {
    this.dialogRef.close(this.selectedDocuments);
  }

  /**
   * Toggle document selection (for checkbox clicks)
   */
  toggleDocumentSelection(document: DocumentItem): void {
    const index = this.selectedDocuments.findIndex(doc => doc.url === document.url);
    if (index > -1) {
      this.selectedDocuments.splice(index, 1);
      const urlIndex = this.selectedImages.indexOf(document.url);
      if (urlIndex > -1) {
        this.selectedImages.splice(urlIndex, 1);
      }
    } else {
      this.selectedDocuments.push(document);
      this.selectedImages.push(document.url);
    }
  }

  /**
   * Check if document is selected
   */
  isDocumentSelected(document: DocumentItem): boolean {
    return this.selectedDocuments.some(doc => doc.url === document.url);
  }

  /**
   * Download all selected images
   */
  async downloadAllSelected(): Promise<void> {
    if (this.selectedImages.length === 0) {
      alert('Please select at least one image to download');
      return;
    }

    console.log(`📥 Starting download of ${this.selectedImages.length} images...`);

    for (let i = 0; i < this.selectedImages.length; i++) {
      const url = this.selectedImages[i];
      const doc = this.displayedDocuments.find(d => d.url === url);
      const fileName = doc?.fileName || `image_${i + 1}`;

      try {
        // Add small delay between downloads to avoid browser blocking
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        this.downloadImage(url, fileName);
        console.log(`✓ Downloaded ${i + 1}/${this.selectedImages.length}: ${fileName}`);
      } catch (error) {
        console.error(`❌ Failed to download ${fileName}:`, error);
      }
    }

    console.log('✅ All downloads initiated');
    alert(`Initiated download of ${this.selectedImages.length} file(s)`);
  }

  /**
   * Toggle view mode between grid and list
   */
  toggleViewMode(): void {
    this.viewMode = this.viewMode === 'grid' ? 'list' : 'grid';
  }

  /**
   * Get date string for display
   */
  getDateString(date: any): string {
    if (!date) return '';

    try {
      const d = date.toDate ? date.toDate() : new Date(date);
      return d.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return '';
    }
  }

  /**
   * Open image in new tab
   */
  openImage(url: string): void {
    window.open(url, '_blank');
  }

  /**
   * Download image
   */
  downloadImage(url: string, fileName: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Close dialog
   */
  onClose(): void {
    this.dialogRef.close(this.selectedDocuments);
  }

  /**
   * Get file extension from URL
   */
  getFileExtension(fileName: string): string {
    const parts = fileName.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  }

  /**
   * Check if file is an image
   */
  isImage(fileName: string): boolean {
    const ext = this.getFileExtension(fileName);
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
  }

  /**
   * Check if file is a PDF
   */
  isPdf(fileName: string): boolean {
    return this.getFileExtension(fileName) === 'pdf';
  }

  /**
   * Get icon for file type
   */
  getFileIcon(fileName: string): string {
    if (this.isImage(fileName)) return 'image';
    if (this.isPdf(fileName)) return 'picture_as_pdf';
    return 'insert_drive_file';
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }
}

